import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { House, CalendarBlank, Broom, Wrench, ChatCircleDots, CurrencyDollar, Robot, ChatText, Sun, ArrowRight, Sparkle, X, Clock } from '@phosphor-icons/react';
import { useNavigate, Link } from 'react-router-dom';

const IMPORT_BANNER_KEY = 'hostcockpit_import_banner_dismissed_v1';

export default function DashboardPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bannerDismissed, setBannerDismissed] = useState(() => localStorage.getItem(IMPORT_BANNER_KEY) === '1');

  useEffect(() => {
    Promise.all([api.get('/dashboard/stats'), api.get('/dashboard/briefing'), api.get('/entitlements')])
      .then(([s, b, e]) => { setStats(s.data); setBriefing(b.data); setEntitlements(e.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [api]);

  const dismissBanner = () => {
    localStorage.setItem(IMPORT_BANNER_KEY, '1');
    setBannerDismissed(true);
  };

  if (loading) return <div className="text-zinc-500 text-sm" data-testid="dashboard-loading">Loading...</div>;

  const cards = [
    { label: 'Properties', value: stats?.total_properties || 0, icon: House, color: 'text-[#0052FF]', bg: 'bg-blue-50', link: '/properties' },
    { label: 'Active Bookings', value: stats?.active_bookings || 0, icon: CalendarBlank, color: 'text-emerald-600', bg: 'bg-emerald-50', link: '/bookings' },
    { label: 'Revenue', value: `$${(stats?.total_revenue || 0).toLocaleString()}`, icon: CurrencyDollar, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'AI Conversations', value: stats?.total_chats || 0, icon: Robot, color: 'text-violet-600', bg: 'bg-violet-50' },
    { label: 'AI Messages Handled', value: stats?.ai_responses || 0, icon: ChatText, color: 'text-cyan-600', bg: 'bg-cyan-50' },
    { label: 'Pending Cleaning', value: stats?.pending_cleaning || 0, icon: Broom, color: 'text-purple-600', bg: 'bg-purple-50', link: '/cleaning' },
    { label: 'Open Maintenance', value: stats?.open_maintenance || 0, icon: Wrench, color: 'text-red-600', bg: 'bg-red-50', link: '/maintenance' },
    { label: 'Total Messages', value: stats?.total_messages || 0, icon: ChatCircleDots, color: 'text-teal-600', bg: 'bg-teal-50' },
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
          Welcome back, {user?.name?.split(' ')[0] || 'Host'}
        </h1>
        <p className="text-sm text-zinc-500 mt-1" style={{ fontFamily: 'var(--font-body)' }}>Here's your property management overview</p>
      </div>

      {/* Trial countdown banner — shows during free 14-day trial or after expiry */}
      {entitlements && entitlements.reason === 'free_trial' && entitlements.trial_active && (
        <div className="mb-6 border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-amber-50/30 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="trial-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-sm flex items-center justify-center flex-shrink-0"><Clock size={20} weight="fill" className="text-white" /></div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-700 mb-0.5">Free trial</div>
              <div className="text-sm font-bold text-zinc-950">
                {entitlements.trial_days_left === 0 ? 'Your free trial ends today' : `${entitlements.trial_days_left} ${entitlements.trial_days_left === 1 ? 'day' : 'days'} left in your free trial`}
              </div>
              <p className="text-xs text-zinc-600 mt-0.5">Includes 1 property. Upgrade anytime to unlock more + all Pro features.</p>
            </div>
          </div>
          <Link to="/billing" className="px-4 py-2 bg-zinc-900 text-white text-xs font-bold rounded-sm hover:bg-zinc-800 inline-flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap" data-testid="trial-banner-upgrade">
            Upgrade now <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      )}
      {entitlements && entitlements.reason === 'trial_expired' && (
        <div className="mb-6 border border-red-200 bg-red-50 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="trial-expired-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-red-600 rounded-sm flex items-center justify-center flex-shrink-0"><Clock size={20} weight="fill" className="text-white" /></div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-red-700 mb-0.5">Trial ended</div>
              <div className="text-sm font-bold text-zinc-950">Your 14-day free trial has ended</div>
              <p className="text-xs text-zinc-600 mt-0.5">Subscribe to keep AI chat, SMS, cleaning workflows, and voice agents running on your property.</p>
            </div>
          </div>
          <Link to="/billing" className="px-4 py-2 bg-[#0052FF] text-white text-xs font-bold rounded-sm hover:bg-[#003EDD] inline-flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap" data-testid="trial-expired-upgrade">
            Pick a plan <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      )}

      {/* Import banner — shown to users with < 2 properties and only if not dismissed */}
      {!bannerDismissed && (stats?.total_properties || 0) < 2 && (
        <div
          className="relative mb-6 overflow-hidden border border-[#0052FF]/20 bg-gradient-to-br from-blue-50 via-white to-blue-50/30 p-5 sm:p-6"
          data-testid="import-banner"
        >
          <button
            onClick={dismissBanner}
            className="absolute top-3 right-3 p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-white/60 rounded-sm transition-colors"
            aria-label="Dismiss"
            data-testid="import-banner-dismiss"
          >
            <X size={14} weight="bold" />
          </button>
          <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
            <div className="w-11 h-11 bg-[#0052FF] rounded-sm flex items-center justify-center flex-shrink-0 shadow-sm">
              <Sparkle size={22} weight="fill" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#0052FF] mb-1">New · AI Importer</div>
              <h3 className="text-lg sm:text-xl font-black text-zinc-950 tracking-tight leading-snug" style={{ fontFamily: 'var(--font-heading)' }}>
                Import your Airbnb or Vrbo listing in 10 seconds
              </h3>
              <p className="text-sm text-zinc-600 mt-1.5 leading-relaxed">
                Paste a listing URL — we'll auto-fill the name, address, bedrooms, nightly rate, and description using AI. Review &amp; save. That's it.
              </p>
            </div>
            <button
              onClick={() => navigate('/properties?import=1')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] transition-colors shadow-sm flex-shrink-0 whitespace-nowrap"
              data-testid="import-banner-cta"
            >
              Import listing <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {/* Daily Briefing */}
      {briefing && (
        <div className="bg-zinc-950 text-white p-5 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="daily-briefing">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-[#0052FF] flex items-center justify-center rounded-sm flex-shrink-0">
              <Sun size={18} weight="bold" />
            </div>
            <div>
              <p className="text-sm font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Today's Briefing</p>
              <p className="text-xs text-zinc-400 leading-relaxed">
                {briefing.checkins_today?.length > 0 ? `${briefing.checkins_today.length} check-in${briefing.checkins_today.length > 1 ? 's' : ''} today` : 'No check-ins today'}
                {' | '}
                {briefing.checkouts_today?.length > 0 ? `${briefing.checkouts_today.length} checkout${briefing.checkouts_today.length > 1 ? 's' : ''}` : 'No checkouts'}
                {' | '}
                {briefing.cleaning_today > 0 ? `${briefing.cleaning_today} cleaning task${briefing.cleaning_today > 1 ? 's' : ''}` : 'No cleaning'}
                {' | '}
                AI handled <span className="text-[#0052FF] font-bold">{briefing.ai_responses_24h}</span> messages in the last 24h
                {briefing.open_tickets > 0 && <span className="text-red-400"> | {briefing.open_tickets} open ticket{briefing.open_tickets > 1 ? 's' : ''}</span>}
              </p>
            </div>
          </div>
          {(briefing.checkins_today?.length > 0 || briefing.checkouts_today?.length > 0) && (
            <div className="flex gap-3 text-xs">
              {briefing.checkins_today?.map((b, i) => (
                <div key={i} className="px-3 py-1.5 bg-emerald-500/20 text-emerald-300 rounded-sm font-bold">
                  IN: {b.guest_name}
                </div>
              ))}
              {briefing.checkouts_today?.map((b, i) => (
                <div key={i} className="px-3 py-1.5 bg-amber-500/20 text-amber-300 rounded-sm font-bold">
                  OUT: {b.guest_name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-200 bg-white mb-8" data-testid="stats-grid">
        {cards.map((c, i) => (
          <div
            key={i}
            onClick={() => c.link && navigate(c.link)}
            className={`p-5 border-b border-r border-zinc-200 ${c.link ? 'cursor-pointer hover:bg-zinc-50' : ''} transition-colors`}
            data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className={`w-9 h-9 ${c.bg} flex items-center justify-center rounded-sm mb-3`}>
              <c.icon size={18} weight="duotone" className={c.color} />
            </div>
            <p className="text-2xl font-black text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>{c.value}</p>
            <p className="text-xs text-zinc-500 font-medium mt-1 uppercase tracking-wide">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* AI Chat Activity by Property */}
        <div className="bg-white border border-zinc-200" data-testid="chat-by-property">
          <div className="px-6 py-4 border-b border-zinc-200">
            <h2 className="text-sm font-bold text-zinc-950 uppercase tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>AI Chat Activity by Property</h2>
          </div>
          {stats?.property_chat_stats?.length > 0 ? (
            <div className="divide-y divide-zinc-100">
              {stats.property_chat_stats.map((p, i) => {
                const maxMsgs = Math.max(...stats.property_chat_stats.map(x => x.messages), 1);
                const pct = (p.messages / maxMsgs) * 100;
                return (
                  <div key={i} className="px-6 py-3" data-testid={`chat-stat-${i}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-sm font-bold text-zinc-900">{p.property_name}</p>
                      <div className="text-right">
                        <span className="text-xs text-zinc-500">{p.sessions} chats &middot; </span>
                        <span className="text-xs font-bold text-[#0052FF]">{p.messages} msgs</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#0052FF] rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <Robot size={32} weight="duotone" className="text-zinc-300 mx-auto mb-3" />
              <p className="text-sm text-zinc-500">No AI conversations yet. Share your property chat links with guests!</p>
            </div>
          )}
        </div>

        {/* Recent Bookings */}
        <div className="bg-white border border-zinc-200" data-testid="recent-bookings">
          <div className="px-6 py-4 border-b border-zinc-200">
            <h2 className="text-sm font-bold text-zinc-950 uppercase tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>Recent Bookings</h2>
          </div>
          {stats?.recent_bookings?.length > 0 ? (
            <div className="divide-y divide-zinc-100">
              {stats.recent_bookings.map((b, i) => (
                <div key={i} className="px-6 py-3 flex items-center justify-between" data-testid={`recent-booking-${i}`}>
                  <div>
                    <p className="text-sm font-bold text-zinc-900">{b.guest_name}</p>
                    <p className="text-xs text-zinc-500">{b.property_name} &middot; {b.check_in} to {b.check_out}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-zinc-900">${b.total_price}</p>
                    <span className={`text-xs px-2 py-0.5 font-bold ${b.status === 'confirmed' ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-600'}`}>{b.status}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <p className="text-sm text-zinc-500">No bookings yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent AI Conversations */}
      <div className="bg-white border border-zinc-200" data-testid="recent-chats">
        <div className="px-6 py-4 border-b border-zinc-200">
          <h2 className="text-sm font-bold text-zinc-950 uppercase tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>Recent AI Conversations</h2>
        </div>
        {stats?.recent_chats?.length > 0 ? (
          <div className="divide-y divide-zinc-100">
            {stats.recent_chats.slice(0, 6).map((c, i) => (
              <div key={i} className="px-6 py-4" data-testid={`recent-chat-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-violet-100 rounded-full flex items-center justify-center">
                      <Robot size={14} weight="duotone" className="text-violet-600" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-zinc-900">{c.guest_name || 'Guest'}</p>
                      <p className="text-xs text-zinc-500">{c.property_name}</p>
                    </div>
                  </div>
                  <span className="text-xs text-zinc-400">{c.messages?.length || 0} messages</span>
                </div>
                <div className="ml-9 space-y-1">
                  {c.messages?.slice(-2).map((m, j) => (
                    <div key={j} className={`text-xs px-3 py-1.5 rounded-sm ${m.role === 'user' ? 'bg-zinc-100 text-zinc-700' : 'bg-blue-50 text-blue-800'}`}>
                      <span className="font-bold">{m.role === 'user' ? c.guest_name || 'Guest' : 'AI'}:</span> {m.content?.substring(0, 150)}{m.content?.length > 150 ? '...' : ''}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-6 py-12 text-center">
            <Robot size={32} weight="duotone" className="text-zinc-300 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">No AI conversations yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}
